import * as React from 'react';

import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'src/rerouter/index';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {useWebAndExtRouterControl} from 'src/components/micro-apps-extension/hooks/useWebAndExtRouterControl';

import {EXTENSION_ID} from 'src/chat-extension/constants';
import logger from 'src/utils/logger';

import PortContext from 'src/components/messaging/port-context';

import {setMeetingsScheduleEventData} from 'src/action-creators/messages';

import {setMsgExtLocationData} from 'src/action-creators/draft-messages';
import {routeExternalContact} from 'src/action-creators/contacts';

import {
  CLICKED_NUMBER,
  PORT_OPEN,
  ANALYTICS_TRACK,
  MEETING_SCHEDULE_EVENT,
  CONVERSATION_LINK_CLICKED,
} from 'src/chat-extension/messages';
import {isAgentSelectedForAmplitudeTracking} from 'src/selectors/messages-v2';

import {AnalyticsService} from 'src/analytics/index';
import {getMessagingTrackingMetrics} from 'src/components/messaging/messaging-amplitude-helper';


const initialExtensionTimeout = 200;

const ExtensionConnection = ({children}: {children: React.ReactNode}) => {
  const {isExtension} = useEnvironmentContext();
  const {changeLocation} = useWebAndExtRouterControl();
  const router = useHistory();
  const dispatch = useDispatch();
  const agency = useSelector((state) => state.agency.agency.name);
  const agencies = ['HCAHealthcare', 'HCA-Infor-Sandbox', 'HCA-Infor-UAT'];
  const trackHCAEvent = agencies.includes(agency);

  const [extensionPortValue, setExtensionPortValue] = React.useState<any>(null);
  const [extensionConnectionTimeout, setExtensionConnectionTimeout] =
    React.useState(initialExtensionTimeout);
  const trackAmplitudeEvent = useSelector(isAgentSelectedForAmplitudeTracking);

  const handleMessage = (message: any) => {
    if (message.type === PORT_OPEN) {
      setExtensionConnectionTimeout(initialExtensionTimeout);
    } else if (message.type === MEETING_SCHEDULE_EVENT) {
      const payload = message.payload;
      setTimeout(() => {
        dispatch(setMeetingsScheduleEventData(payload));
        changeLocation(router, {
          ...router.location,
          pathname: '/meetings/schedule-now',
        });
      }, 0);
    } else if (message.type === CLICKED_NUMBER) {
      setTimeout(() => {
        dispatch(routeExternalContact(router, message));
        if (trackAmplitudeEvent) {
          const {mode, domain, path, fullPath} = message.payload;
          const trackingDetails = getMessagingTrackingMetrics();
          AnalyticsService.track('Extension Number Clicked', {
            ...trackingDetails,
            mode,
            domain,
            path,
            fullPath,
          });
        }
      }, 0);
    } else if (message.type === ANALYTICS_TRACK) {
      const {event, data} = message.payload;
      setTimeout(() => {
        if (event === 'EXTENSION ATS URL') {
          if (trackAmplitudeEvent) {
            // we need to set these details to redux state to fetch
            // as this is need for multiple events
            dispatch(setMsgExtLocationData(data));
          }
        } else {
          if (trackAmplitudeEvent) {
            const trackingDetails = getMessagingTrackingMetrics();
            AnalyticsService.track(event, {
              trackingDetails,
              ...data,
            });
          }
        }
      }, 0);
    } else if (message.type === CONVERSATION_LINK_CLICKED) {
      const conversationLink =
        typeof message.payload?.conversationLink === 'string'
          ? message.payload.conversationLink.trim()
          : '';

      setTimeout(() => {
        changeLocation(router, {
          pathname: '/voiceflow/conversations',
          query: {cl: conversationLink},
        });
      }, 0);
    }
  };

  const handleDisconnect = (port: any) => {
    const error =
      port.error ||
      (window.chrome &&
        window.chrome.runtime &&
        window.chrome.runtime.lastError);
    logger.log('Port disconnecting');
    if (error) {
      logger.error('Disconnected from extension.', error);
    }

    port.onMessage.removeListener(handleMessage);

    logger.log(
      `Attempting to reconnect to extension in ${extensionConnectionTimeout}ms...`,
    );
    setTimeout(() => connectToExtension(), extensionConnectionTimeout);
    setExtensionConnectionTimeout((prev) => prev * 2);
    setExtensionPortValue(null);
  };

  const connectToExtension = () => {
    // @ts-expect-error - TS2339 - Property 'chrome' does not exist on type 'Window & typeof globalThis'.
    const extensionPort = window.chrome.runtime.connect(EXTENSION_ID, {
      name: 'chat-iframe',
    });
    setExtensionPortValue(extensionPort);
    if (extensionPort) {
      extensionPort.onMessage.addListener(handleMessage);
      extensionPort.onDisconnect.addListener(handleDisconnect);
    }
  };

  React.useEffect(() => {
    if (window.chrome && window.chrome.runtime && isExtension) {
      connectToExtension();
    }
  }, []);

  return (
    <PortContext.Provider value={extensionPortValue}>
      {children}
    </PortContext.Provider>
  );
};

export default ExtensionConnection;
